"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from "@workspace/ui/components";
import { Wrench } from "lucide-react";
import { usePathname } from "next/navigation";
import { Suspense, useState } from "react";
import { Panel, PanelResizeHandle } from "react-resizable-panels";
import invariant from "tiny-invariant";
import LoadingSpinner from "../LoadingSpinner";
import { DevPanelTemplateGalleryData } from "./DevPanelTemplateGalleryData";
import { DevPanelTemplateGalleryPlaceholders } from "./DevPanelTemplateGalleryPlaceholders";
import { DevPanelUserData } from "./DevPanelUserData";
import { DevPanelUserLookup } from "./DevPanelUserLookup";
import { DevPanelWorkflowData } from "./DevPanelWorkflowData";
import { DevPanelWorkflowOpenApi } from "./DevPanelWorkflowOpenApi";

export const DevPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const isRunPage = pathname.startsWith("/run/");
  const isTemplateGalleryPage = pathname === "/template-gallery";

  if (!isOpen)
    return (
      <div className="flex h-[100vh] w-auto border-l border-gray-300 bg-white px-1 py-2 dark:border-gray-700">
        <OpenButton onClick={() => setIsOpen(true)} />
      </div>
    );

  return (
    <>
      <PanelResizeHandle className="h-[100vh] bg-gray-300 px-0.5 transition-all hover:bg-blue-600" />
      <Panel
        defaultSize={25}
        minSize={25}
        maxSize={50}
        order={1}
        className="h-[100vh] bg-white px-1 py-2 pb-6"
      >
        <div className="flex items-center gap-1">
          <OpenButton onClick={() => setIsOpen(false)} />
          <h1 className="text-sm font-semibold">Dev Panel</h1>
        </div>
        <Accordion
          type="single"
          collapsible
          className="h-[calc(100vh-40px)] overflow-auto px-2.5"
        >
          {isRunPage && <AccordionItemWorkflowMetadata />}
          {isRunPage && <AccordionItemWorkflowOpenApi />}
          {isTemplateGalleryPage && <AccordionItemTemplateGalleryData />}
          {isTemplateGalleryPage && (
            <AccordionItemTemplateGalleryPlaceholders />
          )}
          <AccordionItemUserData />
          <AccordionItemUserLookup />
        </Accordion>
      </Panel>
    </>
  );
};

// Sub-components

const OpenButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button variant="ghost" size="icon" onClick={onClick} className="p-2">
      <Wrench size={16} />
    </Button>
  );
};

const AccordionItemUserData = () => {
  return (
    <AccordionItem value="user-data">
      <AccordionTrigger>User Data</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelUserData />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};

const AccordionItemUserLookup = () => {
  return (
    <AccordionItem value="user-lookup">
      <AccordionTrigger>User Lookup</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelUserLookup />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};

const AccordionItemWorkflowMetadata = () => {
  const pathname = usePathname();
  const serviceId = pathname.split("/")[2];

  invariant(serviceId, "Service ID is required");

  return (
    <AccordionItem value="workflow-data">
      <AccordionTrigger>Workflow Metadata</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelWorkflowData serviceId={serviceId} />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};

const AccordionItemWorkflowOpenApi = () => {
  const pathname = usePathname();
  const serviceId = pathname.split("/")[2];

  invariant(serviceId, "Service ID is required");
  return (
    <AccordionItem value="workflow-open-api">
      <AccordionTrigger>Workflow OpenAPI</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelWorkflowOpenApi serviceId={serviceId} />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};

const AccordionItemTemplateGalleryData = () => {
  return (
    <AccordionItem value="template-gallery-data">
      <AccordionTrigger>Template Gallery Data</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelTemplateGalleryData />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};

const AccordionItemTemplateGalleryPlaceholders = () => {
  return (
    <AccordionItem value="template-gallery-placeholders">
      <AccordionTrigger>Template Gallery Placeholders</AccordionTrigger>
      <AccordionContent>
        <Suspense fallback={<LoadingSpinner size={16} color="black" />}>
          <DevPanelTemplateGalleryPlaceholders />
        </Suspense>
      </AccordionContent>
    </AccordionItem>
  );
};
